// src/pages/subscription/MyBusinessPage.jsx
import React, { useState, useCallback } from 'react';
import { Building2, Palette, MessageSquare } from 'lucide-react';
import { PageHeader, ThemeSettings } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import BusinessDetails from './components/BusinessDetails';
import BusinessFeatureSettings from './components/BusinessFeatureSettings';
import CurrentSubscription from './components/CurrentSubscription';
import PaymentHistory from './components/PaymentHistory';
import styles from './MyBusinessPage.module.css';

const MyBusinessPage = () => {
  const { t } = useLocalization();
  const { user, getUserPermissions } = useAuth();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [navigationHandler, setNavigationHandler] = useState(null);

  // Check if user has theme management permission
  const hasThemePermission = getUserPermissions().includes('manage_subscription');

  // Handle theme navigation attempt callback
  const handleThemeNavigationAttempt = useCallback((handler) => {
    setNavigationHandler(() => handler);
  }, []);

  // Handle tab change with navigation prevention
  const handleTabChange = async (newTab) => {
    // If leaving theme tab and there's a navigation handler, check for unsaved changes
    if (activeTab === 'theme' && navigationHandler) {
      const canNavigate = await navigationHandler();
      if (!canNavigate) {
        return; // Prevent tab change
      }
    }
    
    setActiveTab(newTab);
  };

  if (!user || !user.business) {
    return (
      <div className={styles.myBusinessPage}>
        <PageHeader
          title={t('subscription.myBusiness')}
          description={t('subscription.manageBusinessSubscription')}
          icon={Building2}
        />
        <div className={styles.errorState}>
          <p>{t('subscription.noBusinessFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.myBusinessPage}>
      <PageHeader
        title={t('subscription.myBusiness')}
        description={t('subscription.manageBusinessSubscription')}
        icon={Building2}
      />

      <div className={styles.tabNavigation}>
        <button
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          {t('subscription.overview')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'payments' ? styles.active : ''}`}
          onClick={() => handleTabChange('payments')}
        >
          {t('subscription.paymentHistory')}
        </button>
        {hasThemePermission && (
          <button
            className={`${styles.tabButton} ${activeTab === 'theme' ? styles.active : ''}`}
            onClick={() => handleTabChange('theme')}
          >
            <Palette size={16} className={styles.tabIcon} />
            {t('subscription.theme')}
          </button>
        )}

        {hasPermission('manage_sms_config') && (
          <button className={styles.tabButton} onClick={() => navigate('/sms-config')}>
            <MessageSquare size={16} />
            SMS Configuration
          </button>
        )}
      </div>

      {activeTab === 'overview' && (
        <div className={styles.overviewTab}>
          <div className={styles.topSection}>
            <BusinessDetails business={user.business} />
            <BusinessFeatureSettings />
          </div>
          <div className={styles.bottomSection}>
            <CurrentSubscription />
          </div>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className={styles.paymentsTab}>
          <PaymentHistory />
        </div>
      )}

      {activeTab === 'theme' && hasThemePermission && (
        <div className={styles.themeTab}>
          <ThemeSettings onNavigationAttempt={handleThemeNavigationAttempt} />
        </div>
      )}
    </div>
  );
};

export default MyBusinessPage;